/* Azéo — accessibility safety net for agentic browsing and keyboard users.
 * Keeps aria-hidden subtrees out of the focus order, including dynamically
 * injected drawers/clones. It only adds inert when a hidden subtree actually
 * contains a focusable element, and removes only inert values that this script added.
 */
(() => {
  "use strict";

  const focusableSelector = [
    "a[href]",
    "area[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "summary",
    "iframe",
    "[contenteditable='true']",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");

  const hasFocusable = (el) => {
    if (!(el instanceof HTMLElement)) return false;
    return el.matches(focusableSelector) || !!el.querySelector(focusableSelector);
  };

  const syncHiddenState = (el) => {
    if (!(el instanceof HTMLElement)) return;

    if (el.getAttribute("aria-hidden") === "true") {
      if (hasFocusable(el) && !el.hasAttribute("inert")) {
        el.setAttribute("inert", "");
        el.dataset.azeoA11yInert = "true";
      }
      return;
    }

    if (el.dataset.azeoA11yInert === "true") {
      el.removeAttribute("inert");
      delete el.dataset.azeoA11yInert;
    }
  };

  const scan = (root = document) => {
    if (root instanceof HTMLElement && root.hasAttribute("aria-hidden")) {
      syncHiddenState(root);
    }
    if (root.querySelectorAll) {
      root.querySelectorAll("[aria-hidden]").forEach(syncHiddenState);
    }
  };

  const cleanVisualCloneIds = (root) => {
    if (!(root instanceof HTMLElement)) return;
    const clone = root.matches(".aztb__group--clone, [data-azeo-visual-clone]")
      ? root
      : root.querySelector?.(".aztb__group--clone, [data-azeo-visual-clone]");
    if (!clone) return;
    clone.removeAttribute("id");
    clone.querySelectorAll("[id]").forEach(el => el.removeAttribute("id"));
    clone.setAttribute("aria-hidden", "true");
    clone.setAttribute("inert", "");
  };

  const init = () => {
    scan();
    document.querySelectorAll(".aztb__group--clone").forEach(cleanVisualCloneIds);

    if (!("MutationObserver" in window)) return;
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes") {
          syncHiddenState(mutation.target);
          continue;
        }
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          cleanVisualCloneIds(node);
          scan(node);
        });
      }
    }).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-hidden"]
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();